import React from 'react';
import LegalLayout from '../../components/LegalLayout';
import { COMPANY, LAST_UPDATED } from './companyConfig';

const Privacy = () => (
  <LegalLayout
    title="Privacy Policy"
    subtitle="How we collect, use, store, and protect your personal information when you use ACF Tourism."
    lastUpdated={LAST_UPDATED}
    canonicalPath="/privacy"
    description="ACF Tourism privacy policy — how we collect, use, store, and protect your personal data under Indian IT and DPDP regulations."
  >
    <h2>1. Scope</h2>
    <p>
      This Privacy Policy describes how <strong>{COMPANY.legalName}</strong> (“{COMPANY.brand}”,
      “we”, “our”) handles the personal information of visitors, customers, and travel-agent
      users of <strong>{COMPANY.domain}</strong>. It applies to data collected through our
      website, mobile web app, emails, and customer support interactions.
    </p>
    <p>
      This policy is published in compliance with the Information Technology Act, 2000, the
      Information Technology (Reasonable security practices and procedures and sensitive
      personal data or information) Rules, 2011, and the Digital Personal Data Protection
      Act, 2023 (as applicable from enforcement).
    </p>

    <h2>2. Information We Collect</h2>
    <h3>2.1 Information you provide</h3>
    <ul>
      <li>Name, email, phone number, date of birth, gender.</li>
      <li>Traveller details (name, DOB, passport for international bookings).</li>
      <li>Billing address; for agents: GSTIN, PAN, state code, agency name.</li>
      <li>Booking details: itinerary, class of travel, special service requests.</li>
      <li>Support tickets and the messages you share with us.</li>
    </ul>
    <h3>2.2 Information collected automatically</h3>
    <ul>
      <li>Device information, IP address, browser type, operating system.</li>
      <li>Pages viewed, search queries, time spent — via cookies and server logs.</li>
      <li>Authentication cookies (HTTP-only, Secure, SameSite) to maintain sessions.</li>
    </ul>
    <h3>2.3 Information from third parties</h3>
    <ul>
      <li>Payment status + transaction references from CCAvenue / payment gateways.</li>
      <li>PNR, e-ticket, and fare-rule data from airlines / consolidators (Tripjack, Riya Travel).</li>
      <li>Fraud / blocklist signals from risk-scoring partners, where applicable.</li>
    </ul>

    <h3>Sensitive data we do <em>not</em> store</h3>
    <p>
      We do <strong>not</strong> store your card number, CVV, UPI PIN, or net-banking password.
      These are handled exclusively by PCI-DSS-compliant payment gateways.
    </p>

    <h2>3. How We Use Your Information</h2>
    <ul>
      <li>Process bookings, issue tickets, and deliver confirmations.</li>
      <li>Provide customer support, refunds, cancellations, and reschedule.</li>
      <li>Send transactional alerts (SMS / email) for booking status, payment, and support.</li>
      <li>Issue GST-compliant tax invoices to B2B agents.</li>
      <li>Detect and prevent fraud, abuse, or violations of these Terms.</li>
      <li>
        With your explicit opt-in, send marketing communications (you can unsubscribe any time).
      </li>
      <li>Comply with legal obligations (tax filings, law-enforcement requests).</li>
    </ul>

    <h2>4. Sharing of Information</h2>
    <p>
      We share your information only to the extent necessary for the services you request:
    </p>
    <ul>
      <li><strong>Airlines / travel suppliers</strong> — to issue and fulfil the ticket.</li>
      <li><strong>Payment gateway</strong> (CCAvenue) — to process transactions.</li>
      <li><strong>Email / SMS providers</strong> (Resend, MSG91) — to deliver transactional messages.</li>
      <li><strong>Cloud hosting & support tools</strong> — to run the Platform.</li>
      <li><strong>Regulators / tax authorities</strong> — where required by law.</li>
    </ul>
    <p>
      We <strong>never sell</strong> your personal information to third parties for their
      own marketing.
    </p>

    <h2>5. Cookies</h2>
    <p>
      We use cookies for (a) essential site functionality, (b) maintaining your login
      session, (c) anonymous analytics. You can disable cookies in your browser settings,
      but some features (booking, wallet) will not work without them.
    </p>

    <h2>6. Data Retention</h2>
    <ul>
      <li>Booking records: retained for up to 8 years to satisfy tax audit requirements.</li>
      <li>Marketing opt-in lists: retained until you unsubscribe.</li>
      <li>Support ticket conversations: retained for 3 years from ticket closure.</li>
      <li>Access logs: rolling 90 days, then anonymised.</li>
    </ul>

    <h2>7. Security</h2>
    <p>
      We implement industry-standard technical and organisational measures: TLS in transit,
      encrypted data at rest on managed databases, bcrypt-hashed passwords, short-lived
      password-reset tokens (SHA-256 hashed, 1-hour expiry, one-time-use), rate-limited
      login endpoints, and least-privilege access for staff.
    </p>

    <h2>8. Your Rights</h2>
    <p>You may, at any time, by emailing us at <a href={`mailto:${COMPANY.grievanceOfficer.email}`}>{COMPANY.grievanceOfficer.email}</a>:</p>
    <ul>
      <li>Request a copy of the personal data we hold about you.</li>
      <li>Ask us to correct inaccurate data.</li>
      <li>Ask us to delete your account data (subject to tax-retention minimums).</li>
      <li>Withdraw consent to marketing communications.</li>
      <li>Object to automated decisions that materially affect you.</li>
    </ul>

    <h2>9. Grievance Officer</h2>
    <p>
      In compliance with Section 5(9) of the IT Rules, 2021:<br/>
      <strong>Name:</strong> {COMPANY.grievanceOfficer.name}<br/>
      <strong>Email:</strong> <a href={`mailto:${COMPANY.grievanceOfficer.email}`}>{COMPANY.grievanceOfficer.email}</a><br/>
      <strong>Phone:</strong> {COMPANY.grievanceOfficer.phone}<br/>
      Complaints are acknowledged within 24 hours and resolved within 15 days.
    </p>

    <h2>10. Children</h2>
    <p>
      The Platform is not intended for children under 18. We do not knowingly collect data
      from minors except where a parent or guardian makes a booking on their behalf
      (traveller details only, no account creation).
    </p>

    <h2>11. Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy. Material changes are notified on the Platform and
      via email to registered users 30 days before taking effect.
    </p>

    <h2>12. Contact</h2>
    <p>
      For any privacy-related queries, email <a href={`mailto:${COMPANY.legalEmail}`}>{COMPANY.legalEmail}</a>.
    </p>
  </LegalLayout>
);

export default Privacy;
